import { useEffect, useState } from 'react';
import { UserMinus } from 'lucide-react';
import {
  formatCourierPackageTag,
  isCourierAssignableOrder,
  isManualApprovalPending,
  useTranslation,
} from '@dukanti/shared';
import type {
  AdminOrder,
  CourierAssignmentResult,
  StoreOrderRow,
  TranslationKey,
} from '@dukanti/shared';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useUnassignOrdersFromCourier } from '@/hooks/use-unassign-orders-from-courier';
import { CourierAssignmentDialog } from './courier-assignment-dialog';

type OrderWithAssignment = AdminOrder & {
  readonly courier_assignment?: StoreOrderRow['courier_assignment'];
};

export interface OrderCourierAssignmentPanelProps {
  readonly order: OrderWithAssignment;
  readonly storeId: string;
  readonly storeName: string;
  readonly canManageCouriers: boolean;
}

export function OrderCourierAssignmentPanel({
  order,
  storeId,
  storeName,
  canManageCouriers,
}: OrderCourierAssignmentPanelProps): React.JSX.Element | null {
  const { t } = useTranslation();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [lastResult, setLastResult] = useState<CourierAssignmentResult | null>(null);
  const [locallyUnassignedOrderId, setLocallyUnassignedOrderId] = useState<string | null>(null);
  const unassignMutation = useUnassignOrdersFromCourier(storeId);

  useEffect(() => {
    setLocallyUnassignedOrderId(null);
  }, [order.id, order.courier_assignment?.batch_order_id]);

  if (!canManageCouriers || order.delivery_method === 'pickup') return null;

  const assignment =
    locallyUnassignedOrderId === order.id ? null : (order.courier_assignment ?? null);
  const batchId = assignment?.batch_id ?? lastResult?.batch?.id ?? null;
  const courierName = assignment?.courier_name ?? lastResult?.batch?.courier.name ?? null;
  const batchPackageCount = assignment?.batch_order_count ?? lastResult?.batch?.order_count ?? null;
  const isEligible = isCourierAssignableOrder(order);
  const isApprovalPending = isManualApprovalPending(order);
  const proofStatus = assignment?.proof_status ?? 'none';
  const proofStatusKey =
    proofStatus === 'internally_confirmed'
      ? 'admin.orders.courierProof.confirmed'
      : proofStatus === 'submitted' || proofStatus === 'awaiting_internal_confirmation'
        ? 'admin.orders.courierProof.awaitingConfirmation'
        : 'admin.orders.courierProof.awaitingProof';

  return (
    <section
      data-testid="order-courier-assignment-panel"
      className="border-border bg-card shadow-admin-card flex flex-col gap-4 rounded-lg border p-6"
    >
      <div>
        <h2 className="font-display text-foreground text-start text-lg font-semibold">
          {t('admin.orders.courierAssignment.assign' as TranslationKey)}
        </h2>
        <p className="text-muted-foreground mt-1 text-start text-sm">
          {t('admin.orders.courierAssignment.noAutoShipNotice' as TranslationKey)}
        </p>
      </div>

      {assignment || courierName ? (
        <div className="flex flex-wrap gap-2">
          <Badge variant="info">
            {t('admin.orders.courierAssignment.assignedTo' as TranslationKey, {
              name: courierName ?? '',
            })}
          </Badge>
          {assignment?.package_tag ? (
            <Badge variant="neutral">
              {t('admin.orders.courierAssignment.packageTag' as TranslationKey, {
                tag: formatCourierPackageTag(assignment.package_tag),
              })}
            </Badge>
          ) : null}
          {batchPackageCount ? (
            <Badge variant="neutral">
              {t('admin.orders.courierAssignment.batchLinkTitleWithCount' as TranslationKey, {
                count: batchPackageCount,
              })}
            </Badge>
          ) : null}
          <Badge
            variant={
              proofStatus === 'internally_confirmed'
                ? 'success'
                : proofStatus === 'none'
                  ? 'neutral'
                  : 'warning'
            }
          >
            {t(proofStatusKey as TranslationKey)}
          </Badge>
        </div>
      ) : null}

      {!isEligible ? (
        <p className="text-muted-foreground text-start text-sm">
          {t(
            isApprovalPending
              ? 'admin.orders.courierAssignment.errors.manualApprovalRequired'
              : ('admin.orders.courierAssignment.notEligible' as TranslationKey),
          )}
        </p>
      ) : (
        <Button type="button" onClick={() => setDialogOpen(true)}>
          {t('admin.orders.courierAssignment.assign' as TranslationKey)}
        </Button>
      )}

      {batchId ? (
        <div className="grid gap-2">
          <Button
            type="button"
            variant="outline"
            disabled={unassignMutation.isPending}
            onClick={() =>
              unassignMutation.mutate(
                { orderIds: [order.id] },
                {
                  onSuccess: (result) => {
                    if (result.success) {
                      setLocallyUnassignedOrderId(order.id);
                      setLastResult(null);
                    }
                  },
                },
              )
            }
          >
            <UserMinus className="size-4" aria-hidden />
            <span>{t('admin.orders.courierAssignment.unassign' as TranslationKey)}</span>
          </Button>
        </div>
      ) : null}

      <CourierAssignmentDialog
        open={dialogOpen}
        storeId={storeId}
        storeName={storeName}
        orders={[order as unknown as StoreOrderRow]}
        onOpenChange={setDialogOpen}
        onAssigned={setLastResult}
      />
    </section>
  );
}
